// Shopify Customer Phone Update Script - Fixed Version with Phone Validation
// Add this script to your theme's customer registration page

class ShopifyPhoneUpdater {
  constructor(apiEndpoint = 'https://node.hob.fashion/api/shopify') {
    this.apiEndpoint = apiEndpoint;
    this.form = null;
    this.phoneInput = null;
    this.emailInput = null;
    this.isProcessing = false;
    
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupFormHandler());
    } else {
      this.setupFormHandler();
    }
  }

  setupFormHandler() {
    // Find the customer registration form
    this.form = document.querySelector('form[action*="customer"]') || 
               document.querySelector('form[action="/account"]') ||
               document.querySelector('#create_customer');
    
    if (!this.form) {
      console.error('Customer registration form not found');
      return;
    }

    // Find the phone and email input fields
    this.phoneInput = this.form.querySelector('input[name="customer[phone]"]') ||
                      this.form.querySelector('#RegisterForm-phone');
    
    this.emailInput = this.form.querySelector('input[name="customer[email]"]') ||
                      this.form.querySelector('#RegisterForm-email');

    if (!this.phoneInput || !this.emailInput) {
      console.error('Phone or email input field not found');
      return;
    }

    // Override form submission
    this.form.addEventListener('submit', (e) => this.handleFormSubmit(e));
  }

  // NEW: Phone number validation and formatting for Indian numbers
  validateAndFormatPhone(phoneNumber) {
    if (!phoneNumber) return null;
    
    // Remove all non-digit characters except +
    let cleanPhone = phoneNumber.replace(/[^\d+]/g, '');
    
    console.log('Original phone:', phoneNumber);
    console.log('Cleaned phone:', cleanPhone);
    
    // If no + prefix, try to add country code
    if (!cleanPhone.startsWith('+')) {
      // Check if it looks like an Indian mobile number (10 digits starting with 6,7,8,9)
      if (cleanPhone.length === 10 && /^[6-9]/.test(cleanPhone)) {
        cleanPhone = '+91' + cleanPhone;
      }
      // Check if it already has 91 prefix (12 digits: 91 + 10 digit mobile)
      else if (cleanPhone.length === 12 && cleanPhone.startsWith('91') && /^91[6-9]/.test(cleanPhone)) {
        cleanPhone = '+' + cleanPhone;
      }
      // Check for Indian landline patterns (11 digits with STD codes)
      else if (cleanPhone.length === 11 && /^0[1-9]/.test(cleanPhone)) {
        // Remove leading 0 and add +91
        cleanPhone = '+91' + cleanPhone.substring(1);
      }
      // Check if it already has 91 + landline (13 digits: 91 + 0 + 10 digits)
      else if (cleanPhone.length === 13 && cleanPhone.startsWith('910')) {
        cleanPhone = '+91' + cleanPhone.substring(3); // Remove 91 and 0, then add +91
      }
      // For other reasonable lengths, assume Indian country code
      else if (cleanPhone.length >= 7 && cleanPhone.length <= 15) {
        cleanPhone = '+91' + cleanPhone;
      } else {
        console.error('Cannot determine country code for phone:', phoneNumber);
        return null;
      }
    }
    
    // Final validation - E.164 format should be 7-15 digits after the +
    const digitsOnly = cleanPhone.substring(1); // Remove +
    if (digitsOnly.length < 7 || digitsOnly.length > 15 || !/^\d+$/.test(digitsOnly)) {
      console.error('Invalid phone format after cleaning:', cleanPhone);
      return null;
    }
    
    // Additional validation for Indian numbers
    if (cleanPhone.startsWith('+91')) {
      const indianNumber = cleanPhone.substring(3); // Remove +91
      
      // Indian mobile numbers: 10 digits starting with 6,7,8,9
      if (indianNumber.length === 10 && /^[6-9]\d{9}$/.test(indianNumber)) {
        console.log('Valid Indian mobile number:', cleanPhone);
        return cleanPhone;
      }
      // Indian landlines: 10-11 digits with various STD codes
      else if (indianNumber.length >= 10 && indianNumber.length <= 11) {
        console.log('Valid Indian landline number:', cleanPhone);
        return cleanPhone;
      } else {
        console.error('Invalid Indian phone number format:', cleanPhone);
        return null;
      }
    }
    
    console.log('Formatted phone:', cleanPhone);
    return cleanPhone;
  }

  async handleFormSubmit(event) {
    // Prevent multiple submissions
    if (this.isProcessing) {
      event.preventDefault();
      return;
    }

    // Clear any previous errors
    this.clearPhoneError();

    const phoneNumber = this.phoneInput.value.trim();
    const customerEmail = this.emailInput.value.trim();
    
    // If no phone number provided, proceed with normal form submission
    if (!phoneNumber || !customerEmail) {
      return; // Let the form submit normally
    }

    // Validate and format phone number
    const formattedPhone = this.validateAndFormatPhone(phoneNumber);
    if (!formattedPhone) {
      // Show error to user and don't proceed
      this.showPhoneError('Please enter a valid Indian phone number (e.g., 9876543210)', true);
      event.preventDefault();
      return;
    }

    // Prevent default form submission
    event.preventDefault();
    this.isProcessing = true;

    // Show loading state
    this.showLoadingState();

    try {
      // Store formatted phone and email for after registration
      sessionStorage.setItem('pending_phone_update', formattedPhone);
      sessionStorage.setItem('pending_customer_email', customerEmail);
      
      // Submit registration form normally
      await this.submitRegistrationForm();
      
      // Wait for customer to be created in Shopify and account setup
      console.log('Waiting for account setup...');
      await this.delay(5000);
      
      // Update phone number using GraphQL mutation
      await this.updateCustomerPhone(customerEmail, formattedPhone);
      
      // Success - reload page and redirect
      window.location.reload();
      
    } catch (error) {
      console.error('Error during registration process:', error);
      this.handleError(error);
    } finally {
      this.isProcessing = false;
    }
  }

  // NEW: Show phone validation error
  showPhoneError(message, isPermanent = false) {
    // Try to find an existing error element or create one
    let errorElement = this.phoneInput.parentNode.querySelector('.phone-error');
    if (!errorElement) {
      errorElement = document.createElement('div');
      errorElement.className = 'phone-error';
      errorElement.style.color = 'white';
      errorElement.style.fontSize = '14px';
      errorElement.style.marginTop = '5px';
      errorElement.style.padding = '5px';
      errorElement.style.backgroundColor = 'black';
      errorElement.style.border = '1px solid black';
      errorElement.style.borderRadius = '4px';
      this.phoneInput.parentNode.appendChild(errorElement);
    }
    errorElement.textContent = message;
    
    // Clear error after 8 seconds unless it's permanent
    if (!isPermanent) {
      setTimeout(() => {
        if (errorElement.parentNode) {
          errorElement.parentNode.removeChild(errorElement);
        }
      }, 8000);
    }
  }

  // NEW: Clear phone error
  clearPhoneError() {
    const errorElement = this.phoneInput.parentNode.querySelector('.phone-error');
    if (errorElement && errorElement.parentNode) {
      errorElement.parentNode.removeChild(errorElement);
    }
  }

  async submitRegistrationForm() {
    return new Promise((resolve, reject) => {
      // Create a new FormData object from the form
      const formData = new FormData(this.form);
      
      // Remove the phone field to avoid Shopify validation issues
      formData.delete('customer[phone]');

      // Submit the form via fetch
      fetch(this.form.action || '/account', {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8',
        },
        credentials: 'same-origin'
      })
      .then(response => {
        if (response.ok || response.redirected) {
          console.log('Customer registration successful');
          resolve(response);
        } else {
          reject(new Error(`Registration failed: ${response.status}`));
        }
      })
      .catch(reject);
    });
  }

  async updateCustomerPhone(customerEmail, phoneNumber) {
    try {
      // Step 1: Find customer by email using the customers query
      const findCustomerQuery = `
        query {
          customers(first: 1, query: "email:${customerEmail}") {
            edges {
              node {
                id
                email
                phone
                firstName
                lastName
              }
            }
          }
        }
      `;

      console.log('Finding customer by email:', customerEmail);
      console.log('Phone number to update:', phoneNumber);
      
      const findResponse = await fetch(this.apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          query: findCustomerQuery,
          variables: {}
        })
      });

      if (!findResponse.ok) {
        throw new Error(`Find customer API failed: ${findResponse.status}`);
      }

      const findResult = await findResponse.json();
      console.log('Find customer result:', findResult);
      
      if (findResult.errors) {
        throw new Error(`Find customer GraphQL errors: ${JSON.stringify(findResult.errors)}`);
      }

      if (!findResult.data?.customers?.edges?.length) {
        throw new Error('Customer not found with email: ' + customerEmail);
      }

      const customer = findResult.data.customers.edges[0].node;
      console.log('Found customer:', customer);

      // Step 2: Update customer phone using customerUpdate mutation
      const updateMutation = `
        mutation customerUpdate($input: CustomerInput!) {
          customerUpdate(input: $input) {
            customer {
              id
              email
              phone
              firstName
              lastName
            }
            userErrors {
              field
              message
            }
          }
        }
      `;

      console.log('Updating customer phone number to:', phoneNumber);
      
      const updateResponse = await fetch(this.apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          query: updateMutation,
          variables: {
            input: {
              id: customer.id,
              phone: phoneNumber
            }
          }
        })
      });

      if (!updateResponse.ok) {
        throw new Error(`Update customer API failed: ${updateResponse.status}`);
      }

      const updateResult = await updateResponse.json();
      console.log('Update customer result:', updateResult);
      
      if (updateResult.errors) {
        throw new Error(`Update customer GraphQL errors: ${JSON.stringify(updateResult.errors)}`);
      }

      if (updateResult.data?.customerUpdate?.userErrors?.length > 0) {
        const errors = updateResult.data.customerUpdate.userErrors;
        console.error('Customer update validation errors:', errors);
        
        // Create a more detailed error message
        const errorMessages = errors.map(error => error.message).join(', ');
        throw new Error(`Customer update errors: ${errorMessages}`);
      }

      console.log('Phone number updated successfully:', updateResult.data?.customerUpdate?.customer);
      
      // Clear stored data
      sessionStorage.removeItem('pending_phone_update');
      sessionStorage.removeItem('pending_customer_email');
      
      return updateResult.data?.customerUpdate?.customer;

    } catch (error) {
      console.error('Phone update error:', error);
      throw error;
    }
  }

  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  showLoadingState() {
    // Disable the submit button and show loading
    const submitButton = this.form.querySelector('button[type="submit"]');
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.textContent = 'Creating Account...';
    }
  }

  handleError(error) {
    // Re-enable the submit button
    const submitButton = this.form.querySelector('button[type="submit"]');
    if (submitButton) {
      submitButton.disabled = false;
      submitButton.textContent = 'Create Account'; // Reset to original text
    }
    
    // Parse error message to show specific user-friendly messages
    const errorMessage = error.message || error.toString();
    
    if (errorMessage.includes('Phone has already been taken')) {
      this.showPhoneError('This phone number is already registered with another account. Please use a different number.', true);
    } else if (errorMessage.includes('Phone is invalid')) {
      this.showPhoneError('Please enter a valid Indian phone number (e.g., 9876543210)', true);
    } else if (errorMessage.includes('Customer not found')) {
      this.showPhoneError('Account registration failed. Please try again.', true);
    } else if (errorMessage.includes('Registration failed')) {
      this.showPhoneError('Account registration failed. Please check your details and try again.', true);
    } else {
      // Generic error for other issues
      this.showPhoneError('Registration failed. Please try again or contact support.', true);
    }
    
    console.error('Registration error:', error);
  }
}

// Initialize the phone updater when the script loads
const phoneUpdater = new ShopifyPhoneUpdater();

// Handle cases where registration was completed but phone update is pending
window.addEventListener('load', function() {
  // Check if there's a pending phone update
  const pendingPhone = sessionStorage.getItem('pending_phone_update');
  const customerEmail = sessionStorage.getItem('pending_customer_email');
  
  if (pendingPhone && customerEmail) {
    console.log('Found pending phone update, attempting now...');
    console.log('Pending phone:', pendingPhone);
    
    // Create a temporary updater instance to handle the update
    const tempUpdater = new ShopifyPhoneUpdater();
    
    tempUpdater.updateCustomerPhone(customerEmail, pendingPhone)
      .then(result => {
        console.log('Delayed phone update successful:', result);
        // Reload page after successful update
        window.location.reload();
      })
      .catch(error => {
        console.error('Delayed phone update failed:', error);
        // Clear the stored data and reload
        sessionStorage.removeItem('pending_phone_update');
        sessionStorage.removeItem('pending_customer_email');
        window.location.reload();
      });
  }
});

// Utility function for manual phone updates with validation
window.updateCustomerPhone = async function(email, phone) {
  const updater = new ShopifyPhoneUpdater();
  const formattedPhone = updater.validateAndFormatPhone(phone);
  
  if (!formattedPhone) {
    console.error('Invalid phone number format:', phone);
    return null;
  }
  
  try {
    const result = await updater.updateCustomerPhone(email, formattedPhone);
    console.log('Manual phone update successful:', result);
    return result;
  } catch (error) {
    console.error('Manual phone update failed:', error);
    return null;
  }
};